import { Component, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { finalize, forkJoin, of } from 'rxjs';
import { Question } from 'src/app/models/question';
import { AuthService } from 'src/app/services/auth.service';
import { ConfigService } from 'src/app/services/config.service';
import { ToolsService } from 'src/app/services/tools.service';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { ModalController } from '@ionic/angular';
import { OtpVerificationModalComponent } from '../otp-verification-modal/otp-verification-modal.component';
import { AlertController } from '@ionic/angular';

@Component({
  selector: 'app-update-data',
  templateUrl: './update-data.component.html',
  styleUrls: ['./update-data.component.scss'],
})
export class UpdateDataComponent implements OnInit {
  questions: Array<Question> = [];
  loading: boolean = false;
  loadingFields: boolean = false;
  fieldsToUpdate: string[] = [];
  incrementalFields: string[] = [];
  profileExists: boolean = false;
  userId: number | null = null;
  bannerData: any = { banners: [] };
  loginMethods: any;
  captcha: string = '';

  constructor(
    private configService: ConfigService,
    private toolsService: ToolsService,
    private location: Location,
    private authService: AuthService,
    private router: Router,
    private recaptchaV3Service: ReCaptchaV3Service,
    private modalCtrl: ModalController,
    private alertCtrl:AlertController
  ) {}

  ngOnInit() {
    this.loadUserId();
    this.loadUpdateFields();
    // console.log()
  }

  loadUserId() {
    this.authService.getUserInfo().subscribe({
      next: (response) => {
        this.userId = response?.id ?? null;
      },
      error: () => {
        this.userId = null;
      },
    });
  }

  close() {
    this.router.navigate(['/'], {
      queryParams: { update_data: null, from_update_data: true },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  loadUpdateFields() {
    this.loadingFields = true;

    this.configService.getUpdateDataFields().subscribe({
      next: (data: any) => {
        this.fieldsToUpdate = data?.fields_to_update || [];
        this.incrementalFields = data?.incremental_fields || [];
        this.bannerData = { banners: data?.banners || [] };
        this.loadMaskedFields();
      },
      error: (err) => {
        console.error('Error loading update-data-fields', err);
        this.loadingFields = false;
        this.toolsService.presentErrorToast(
          'Ocurrió un error inesperado, intenta de nuevo más tarde',
        );
      },
    });
  }

  loadMaskedFields() {
    const payload = {
      fields_to_update: this.fieldsToUpdate,
      incremental_fields: this.incrementalFields,
    };

    this.configService.getMaskedProfileFields(payload).subscribe({
      next: (response: any) => {
        const data = response?.data;
        // console.log(data)
        this.profileExists = !!data?.profile_exists;
        const profileFields = data?.profile_fields || {};
        const incrementalQuestions = data?.incremental_questions || {};
        this.questions = this.buildQuestions(
          profileFields,
          incrementalQuestions,
        );
        this.loadingFields = false;
      },
      error: (err) => {
        console.error('Error loading masked fields', err);
        this.loadingFields = false;
        this.toolsService.presentErrorToast(
          'Ocurrió un error inesperado, intenta de nuevo más tarde',
        );
      },
    });
  }

  buildQuestions(
    profileFields: any,
    incrementalQuestions: any,
  ): Array<Question> {
    const questions: Array<Question> = [];
    const incrementalMap = Array.isArray(incrementalQuestions)
      ? incrementalQuestions.reduce((acc: any, item: any) => {
          const key = item?.id;
          if (key) acc[key] = item;
          return acc;
        }, {})
      : incrementalQuestions && typeof incrementalQuestions === 'object'
        ? incrementalQuestions
        : {};

    const fieldsList = this.fieldsToUpdate.length
      ? [
          ...this.fieldsToUpdate,
          ...Object.keys(incrementalMap || {}).filter(
            (field) => !this.fieldsToUpdate.includes(field),
          ),
        ]
      : Array.from(
          new Set([
            ...Object.keys(profileFields || {}),
            ...Object.keys(incrementalMap || {}),
          ]),
        );

    for (const fieldName of fieldsList) {
      const isIncremental = !!incrementalMap[fieldName];
      const fieldInfo =
        profileFields[fieldName] || incrementalMap[fieldName] || {};
      const questionType = String(fieldInfo.type || 'text').toLowerCase();
      const maskedValue = fieldInfo.value || '';
      const isRequired =
        fieldInfo.required === true &&
        (fieldInfo.has_value === false || !fieldInfo.value);

      const labelBase = fieldInfo.label || this.getFieldLabelBase(fieldName);
      const label = this.getFieldLabel(
        labelBase,
        maskedValue,
        isRequired,
        questionType
      );

      let validators: any[] = [];
      let options = null;
      let maxDate: Date = new Date();

      let errorTextValues: any = {
        required: 'Este campo es obligatorio',
        email:
          'Por favor, introduce una dirección de correo electrónico válida',
      };

      const validationBlock = fieldInfo.validation || fieldInfo.validations;
      if (validationBlock) {
        for (const validation in validationBlock) {
          if (validation === 'maxDate') {
            maxDate = new Date(validationBlock[validation]);
          } else if (validation === 'minLength') {
            validators.push(Validators.minLength(validationBlock[validation]));
            errorTextValues['minLength'] =
              `La longitud mínima permitida es ${validationBlock[validation]}`;
          } else if (validation === 'maxLength') {
            validators.push(Validators.maxLength(validationBlock[validation]));
            errorTextValues['maxLength'] =
              `La longitud máxima permitida es ${validationBlock[validation]}`;
          } else if (validation === 'pattern') {
            validators.push(Validators.pattern(validationBlock[validation]));
            errorTextValues['pattern'] = `El formato no es válido`;
          }
        }
      }

      let formControl: FormControl = new FormControl(null, validators);

      if (questionType === 'number') {
        isRequired && validators.push(Validators.required);
        formControl = new FormControl(null, validators);
      } else if (questionType === 'text') {
        isRequired && validators.push(Validators.required);
        formControl = new FormControl(null, validators);
      } else if (questionType === 'email') {
        isRequired && validators.push(Validators.required);
        validators.push(Validators.email);
        formControl = new FormControl(null, validators);
      } else if (questionType === 'phone') {
        isRequired && validators.push(Validators.required);
        formControl = new FormControl(null, validators);
      } else if (questionType === 'select') {
        let data: any[] = [];
        if (typeof fieldInfo?.options === 'string') {
          data = fieldInfo.options.split(',');
        } else if (Array.isArray(fieldInfo.options)) {
          data = fieldInfo.options;
        } else if (
          typeof fieldInfo?.options === 'object' &&
          fieldInfo.options !== null
        ) {
          data = Object.keys(fieldInfo.options);
        }
        if (!data.length && fieldName === 'gender') {
          data = ['Masculino', 'Femenino', 'No binario', 'Prefiero no decirlo'];
        }
        options = data.map((option: any) => {
          return { value: option, label: option };
        });
        if (isRequired) {
          validators.push(Validators.required);
        }
        formControl = new FormControl(null, validators);
      } else if (questionType === 'check') {
        formControl = new FormControl(false);
      } else if (questionType === 'date') {
        isRequired && validators.push(Validators.required);
        formControl = new FormControl(null, validators);
        if (validationBlock?.maxDate) {
          maxDate = new Date(validationBlock.maxDate);
        }
      } else if (questionType === 'dynamic-select') {
        isRequired && validators.push(Validators.required);
        formControl = new FormControl(null, validators);
      }

      let questionLabel = label;

      const questionFinal: Question = {
        id: fieldName,
        label: questionLabel,
        placeholder: maskedValue || '',
        type: (questionType === 'number'
          ? 'text'
          : questionType === 'check'
            ? 'checkbox'
            : questionType === 'phone'
              ? 'tel'
              : questionType) as any,
        formControl: formControl,
        multiple: false,
        options: options as any,
        maxDate: maxDate.toISOString(),
        inputType:
          questionType === 'check' ? 'checkbox' : (questionType as any),
        errorText: errorTextValues,
        suggestions: fieldInfo.suggestions,
        url: fieldInfo.url,
        depends_on: fieldInfo.depends_on || null,
        minDate: validationBlock?.minDate || null,
        required: isRequired,
        incremental_info: isIncremental,
        answer_id: fieldInfo.answer_id,
      };

      questions.push(questionFinal);
    }

    return questions;
  }

  getFieldLabel(
    baseLabel: string,
    maskedValue: string,
    isRequired: boolean,
    fieldType?: string,
  ): string {
    const requiredSuffix = isRequired ? ' *' : '';
    if (maskedValue && fieldType === 'phone') {
      return `${baseLabel} (${maskedValue})${requiredSuffix}`;
    }
    return `${baseLabel}${requiredSuffix}`;
  }

  getFieldLabelBase(fieldName: string): string {
    const labelMap: Record<string, string> = {
      born_date: 'Fecha de nacimiento',
      email: 'Correo electrónico',
      first_name: 'Nombre',
      gender: 'Género',
      last_name: 'Apellidos',
      phone: 'Celular',
    };

    if (labelMap[fieldName]) return labelMap[fieldName];

    const sanitized = fieldName.replace(/_/g, ' ');
    return sanitized.charAt(0).toUpperCase() + sanitized.slice(1);
  }

  mapFieldType(fieldName: string, fieldType: string): Question['type'] {
    if (fieldName === 'phone' || fieldType === 'phone') return 'tel';
    if (fieldName === 'email') return 'email';
    if (fieldType === 'email') return 'text';
    if (fieldType === 'dynamic-select') return 'dynamic-select';
    if (fieldType === 'date') return 'date';
    if (fieldType === 'number') return 'text';
    if (fieldType === 'check') return 'checkbox';
    if (fieldType === 'select') return 'select';
    return 'text';
  }

  getFieldOptions(fieldName: string, fieldType: string, fieldDef?: any) {
    if (fieldType !== 'select') return null;

    if (Array.isArray(fieldDef?.options)) {
      return fieldDef.options.map((option: any) => ({
        value: option,
        label: option,
      }));
    }

    if (fieldName === 'gender') {
      return [
        { value: 'Masculino', label: 'Masculino' },
        { value: 'Femenino', label: 'Femenino' },
        { value: 'No binario', label: 'No binario' },
        { value: 'Prefiero no decirlo', label: 'Prefiero no decirlo' },
      ];
    }

    return null;
  }

  getFieldValidators(
    fieldName: string,
    mappedType: Question['type'],
    isRequired: boolean,
    validation?: any,
  ) {
    const validators = [];

    if (isRequired && mappedType !== 'checkbox') {
      validators.push(Validators.required);
    }

    if (fieldName === 'email' || mappedType === 'email') {
      validators.push(Validators.email);
    }

    if (validation?.minLength) {
      validators.push(Validators.minLength(validation.minLength));
    }
    if (validation?.maxLength) {
      validators.push(Validators.maxLength(validation.maxLength));
    }
    if (validation?.pattern) {
      validators.push(Validators.pattern(validation.pattern));
    }

    return validators;
  }

  getInputType(mappedType: Question['type']): Question['inputType'] {
    if (mappedType === 'email') return 'email';
    if (mappedType === 'tel') return 'tel';
    if (mappedType === 'date') return 'text';
    return 'text';
  }

  validateCaptcha() {
    if (this.configService.mall?.captcha_token) {
      this.recaptchaV3Service.execute('login').subscribe((token: string) => {
        this.captcha = token;
      });
    }
  }

  async submit() {
    this.validateCaptcha();

    let hasError = false;
    for (const question of this.questions) {
      if (question.formControl.invalid) {
        question.formControl.markAsTouched();
        hasError = true;
      }
    }
    if (hasError) return;

    try {
      await this.getLoginMethods();
    } catch (error) {
      this.toolsService.presentErrorToast('Error al obtener métodos de autenticación.');
      return;
    }

    const emailQuestion = this.questions.find((q) => q.type === 'email');
    const phoneQuestion = this.questions.find((q) => q.type === 'tel');

    const isValidContact = await this.handleContactValidation(
      emailQuestion,
      phoneQuestion
    );

    if (!isValidContact) return;

    const standardPayload: any = {};
    const incrementalPayload: any[] = [];

    for (const question of this.questions) {
      const value = question.formControl.value;
      const hasValue = value !== null && value !== undefined && value !== '';
      if (!hasValue) continue;

      if (question.incremental_info) {
        const hasAnswerId =
          question.answer_id !== null &&
          question.answer_id !== undefined &&
          question.answer_id !== '';

        incrementalPayload.push({
          id: hasAnswerId ? question.answer_id : question.id,
          answer: value,
        });
      } else {
        standardPayload[question.id] = value;
      }
    }

    if (
      Object.keys(standardPayload).length === 0 &&
      incrementalPayload.length === 0
    ) {
      this.toolsService.presentErrorToast(
        'Debes completar al menos un campo para continuar'
      );
      return;
    }

    const loginData = this.configService.loadUserLoginData();
    const docType = this.configService.document_type || loginData.document_type;
    const docNumber =
      this.configService.document_number || loginData.document_number;

    this.loading = true;
    const requests = [];

    if (Object.keys(standardPayload).length > 0) {
      if (docType) standardPayload.document_type = docType;
      if (docNumber) standardPayload.document = docNumber;
      requests.push(this.configService.updateProfileData(standardPayload));
    }

    if (incrementalPayload.length > 0) {
      if (!this.userId) {
        this.loading = false;
        this.toolsService.presentErrorToast(
          'No se pudo identificar el usuario para actualizar datos incrementales'
        );
        return;
      }

      requests.push(
        this.configService.updateIncrementalProfile({
          user_id: this.userId,
          answers: incrementalPayload,
        })
      );
    }

    forkJoin(requests.length ? requests : [of(null)])
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.toolsService.presentSucessToast(
            'Datos actualizados correctamente'
          );

          this.router.navigate(['/'], {
            queryParams: { update_data: null, from_update_data: true },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        },
        error: (err) => {
          console.error('Error updating profile data', err);
          this.toolsService.presentErrorToast(
            'Ocurrió un error inesperado, intenta de nuevo más tarde'
          );
        },
      });
  }

  async getLoginMethods(): Promise<any> {
    const data = {
      document: localStorage.getItem('document_number'),
      type_document: localStorage.getItem('document_type'),
      captcha: this.captcha
    };

    try {
      this.loginMethods = await this.authService.getLoginMethods(data);
      // console.log('Login methods obtenidos:', this.loginMethods);
      return this.loginMethods;
    } catch (error) {
      console.error('Error al obtener métodos de login:', error);
      throw error;
    }
  }

  async requestOtpCode(selectedMethod: 'sms' | 'email'): Promise<boolean> {
    const hasMethod = this.loginMethods?.some(
      (item: any) => item.method === selectedMethod
    );

    if (!hasMethod) {
      const isSms = selectedMethod === 'sms';

      const missingMethodName = isSms ? 'un número de teléfono' : 'un correo electrónico';

      const verificationMethod = isSms ? 'correo electrónico' : 'SMS';

      const alert = await this.alertCtrl.create({
        header: 'Método no disponible',
        message: `No cuentas con ${missingMethodName} registrado para recibir el código de verificación por esta vía. Por seguridad, la actualización de un dato de contacto debe verificarse utilizando el otro medio registrado. Como no es posible realizar esta validación, por favor acércate al punto de información del centro comercial para actualizar este dato.`,
        backdropDismiss: false,
        buttons: ['Entendido'],
      });

      await alert.present();
      return false;
    }

    const data = {
      document: localStorage.getItem('document_number'),
      type_document: localStorage.getItem('document_type'),
      method: selectedMethod,
      captcha: this.captcha,
    };

    try {
      await this.authService.sendCode(data);
      this.toolsService.presentSucessToast(
        `Código OTP enviado exitosamente por ${selectedMethod.toUpperCase()}`
      );
      return true;
    } catch (error) {
      console.error(`Error enviando OTP por ${selectedMethod}:`, error);
      this.toolsService.presentErrorToast('Error al solicitar el código OTP.');
      return false;
    }
  }

  async handleContactValidation(emailQuestion: any, phoneQuestion: any): Promise<boolean> {
    const hasEmailValue = !!emailQuestion?.formControl?.value?.toString().trim();
    const hasPhoneValue = !!phoneQuestion?.formControl?.value?.toString().trim();

    if (hasEmailValue && hasPhoneValue) {
      const alert = await this.alertCtrl.create({
        header: 'No puedes cambiar ambos datos',
        message:
          'Por seguridad, no es posible cambiar el correo electrónico y el número de teléfono al mismo tiempo. Actualiza primero uno de estos datos y, una vez validado el cambio, podrás modificar el otro.',
        backdropDismiss: false,
        buttons: ['Entendido'],
      });

      await alert.present();
      return false;
    }

    let methodToSend: 'sms' | 'email' | null = null;

    if (hasEmailValue) {
      methodToSend = 'sms';
    } else if (hasPhoneValue) {
      methodToSend = 'email';
    } else {

      methodToSend = await this.selectOtpMethod();

      if (!methodToSend) {
        this.toolsService.presentErrorToast('Debes seleccionar un método para recibir el código.');
        return false;
      }
    }

    const methodObj = this.loginMethods?.find((m: any) => m.method === methodToSend);
    const destination = methodObj?.value || '';

    const codeSent = await this.requestOtpCode(methodToSend);
    if (!codeSent) return false;

    const isOtpValid = await this.openOtpModal(methodToSend, destination);
    return isOtpValid;
  }

  async openOtpModal(method: any, destination: any): Promise<boolean> {
    const modal = await this.modalCtrl.create({
      component: OtpVerificationModalComponent,
      componentProps: {
        method: method,
        destination: destination
      },
      backdropDismiss: false,
      cssClass: 'otp-modal-style'
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();

    return !!(data && data.verified);
  }

  async selectOtpMethod(): Promise<'sms' | 'email' | null> {
    const hasSms = this.loginMethods?.some((m: any) => m.method === 'sms');
    const hasEmail = this.loginMethods?.some((m: any) => m.method === 'email');

    const smsValue = this.loginMethods?.find((m: any) => m.method === 'sms')?.value || '';
    const emailValue = this.loginMethods?.find((m: any) => m.method === 'email')?.value || '';

    const inputs: any[] = [];

    if (hasSms) {
      inputs.push({
        type: 'radio',
        label: `SMS (${smsValue})`,
        value: 'sms',
        checked: true,
      });
    }

    if (hasEmail) {
      inputs.push({
        type: 'radio',
        label: `Correo electrónico (${emailValue})`,
        value: 'email',
        checked: !hasSms,
      });
    }

    if (inputs.length === 0) {
      this.toolsService.presentErrorToast('No hay métodos de envío disponibles.');
      return null;
    }

    const alert = await this.alertCtrl.create({
      header: 'Verificación requerida',
      subHeader: 'Selecciona dónde deseas recibir el código OTP:',
      inputs,
      backdropDismiss: false,
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel',
          handler: () => {
          },
        },
        {
          text: 'Enviar código',
          handler: (selectedMethod) => selectedMethod,
        },
      ],
    });

    await alert.present();

    const { data, role } = await alert.onDidDismiss();

    if (role === 'cancel' || !data?.values) {
      return null;
    }

    return data.values;
  }
}
